'use client';

import Link from 'next/link';
import { useState } from 'react';
import { usePathname } from 'next/navigation';

const Header = () => {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { label: 'Início', href: '/landing-page' },
    { label: 'Diagnóstico', href: '/multi-step-questionnaire' },
    { label: 'Resultados', href: '/results-page' },
    { label: 'Sobre nós', href: 'https://www.gestpub.pt/sobre-nos/', external: true },
    { label: 'Contactos', href: 'https://www.gestpub.pt/contactos/', external: true },
  ];

  return (
    <header className="w-full" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
      {/* Top bar — dark #272727 */}
      <div style={{ backgroundColor: '#272727' }}>
        <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.05em' }}>
              Diagnóstico de Capacidade Organizacional Municipal
            </span>
          </div>
          <div className="flex items-center gap-5">
            <a
              href="mailto:geral@gestpub.pt"
              className="flex items-center gap-1.5 transition-colors duration-200 hover:text-white"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)' }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              geral@gestpub.pt
            </a>
            <a
              href="tel:+351214846546"
              className="flex items-center gap-1.5 transition-colors duration-200 hover:text-white"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)' }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.06 6.06l1.79-1.79a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              +351 214 846 546
            </a>
          </div>
        </div>
      </div>

      {/* Main nav — white with bottom border */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-0 flex items-center justify-between">
          {/* Logo */}
          <Link
            href="/landing-page"
            className="flex items-center py-3 focus-visible:outline-none"
            aria-label="GestPub — Gestão Pública e Consultoria Autárquica — Página inicial"
          >
            <img
              src="/assets/images/6-1773621431219.png"
              alt="GestPub — Gestão Pública e Consultoria Autárquica"
              style={{ height: 40, width: 'auto', display: 'block', flexShrink: 0, mixBlendMode: 'multiply' }}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Navegação principal">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href;
              return link?.external ? (
                <a
                  key={link?.label}
                  href={link?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-link px-4 py-5 text-sm"
                  style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500, color: '#4B5563' }}
                >
                  {link?.label}
                </a>
              ) : (
                <Link
                  key={link?.label}
                  href={link?.href}
                  className={`nav-link px-4 py-5 text-sm${isActive ? ' active' : ''}`}
                  style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500, color: isActive ? '#009688' : '#4B5563' }}
                >
                  {link?.label}
                </Link>
              );
            })}
            <Link
              href="/multi-step-questionnaire"
              className="ml-4 inline-flex items-center justify-center gap-2 px-5 py-2.5 text-white text-sm font-semibold transition-all duration-200"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                borderRadius: '5px',
                backgroundColor: '#272727',
                border: '2px solid #272727',
                letterSpacing: '0.01em',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#1a1a1a';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#1a1a1a';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#272727';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#272727';
              }}
            >
              Iniciar Diagnóstico
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 transition-colors"
            style={{ color: '#6B7280' }}
            aria-label="Menu"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {mobileOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white">
            <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col gap-1">
              {navLinks?.map((link) =>
                link?.external ? (
                  <a
                    key={link?.label}
                    href={link?.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2.5 text-sm transition-colors duration-200"
                    style={{ fontFamily: 'DM Sans, sans-serif', color: '#4B5563' }}
                    onClick={() => setMobileOpen(false)}
                  >
                    {link?.label}
                  </a>
                ) : (
                  <Link
                    key={link?.label}
                    href={link?.href}
                    className="py-2.5 text-sm transition-colors duration-200"
                    style={{ fontFamily: 'DM Sans, sans-serif', color: pathname === link?.href ? '#009688' : '#4B5563' }}
                    onClick={() => setMobileOpen(false)}
                  >
                    {link?.label}
                  </Link>
                )
              )}
              <Link
                href="/multi-step-questionnaire"
                className="mt-3 inline-flex items-center justify-center px-5 py-2.5 text-white text-sm font-semibold transition-all duration-200"
                style={{ fontFamily: 'DM Sans, sans-serif', borderRadius: '5px', backgroundColor: '#272727', border: '2px solid #272727' }}
                onClick={() => setMobileOpen(false)}
              >
                Iniciar Diagnóstico
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;